import type {
    AugmentedRequest,
    DataSourceRequest,
    DeleteRequest,
    GetRequest,
    HeadRequest,
    PatchRequest,
    PostRequest,
    PutRequest,
    RequestOptions,
} from '@apollo/datasource-rest';
import { RESTDataSource } from '@apollo/datasource-rest';
import { KeyValueCache } from '@apollo/utils.keyvaluecache';
import { endpointOverridesConfig } from '@tn/abtests';
import { CookieValueTypes } from 'cookies-next';
import { NextApiRequest, NextApiResponse } from 'next';

// Structural logger interface: accepts the foundation `appLogger`
// (`(msg, ctx?, err?)`), the legacy Winston-shaped object returned by the
// deprecated `createLogger` shim, and a real Winston `Logger` instance
// (variadic `(message: any, ...meta: any[])`).
//
// - First arg is `unknown` to match Winston's permissive contract — pre-existing
//   call sites pass arrays, errors, or formatted strings interchangeably.
// - Return type is `unknown` (not `void`) because Winston's methods return
//   `Logger` (chainable), while `appLogger` and the shim return `void`. Using
//   `unknown` accepts both shapes without forcing callers to drop chaining;
//   it also signals that callers should NOT depend on a return value
//   (`unknown` can't be used directly). Narrowing to `void` is a candidate
//   for the PTVWS-4134 cleanup pass once chaining call sites are confirmed gone.
//
// Exported so subclasses can opt out of narrowing back to the underlying
// instance's strict signature.
export type Logger = {
    error(message: unknown, ...args: unknown[]): unknown;
    warn(message: unknown, ...args: unknown[]): unknown;
    info(message: unknown, ...args: unknown[]): unknown;
    debug(message: unknown, ...args: unknown[]): unknown;
};

import { Configuration } from '@/entities/regional-configuration/types';
import { ActiveTests } from '@/hooks/providers/useABTest';
import { isAppDebug } from '@/utils/appDebug';
import { AUTH_COOKIE_KEY } from '@/utils/constants/common';
import { getPlatformType } from '@/utils/server/platform-type-utils';
import throwGraphQLError from '@/utils/throwGraphQLError';

type OptionsType = { headers?: Record<string, string>; params?: Record<string, string> };

const DEFAULT_API_VERSION = 'v3.0';

export type BaseContext = {
    req: NextApiRequest;
    res: NextApiResponse;
    authCookie: CookieValueTypes;
    region: string;
    lob: string;
    /**
     * Browser-resolved locale (from getLocale). NOT forwarded globally — individual data sources
     * that localize (e.g. NavApi for global/menu.json) opt in by adding it to their own request
     * params, so region-scoped configurator/curation endpoints are unaffected.
     */
    locale?: string;
    // Platform start session id.
    sessionId?: string;
    // Platform session JWT; only set when the client sends `x-session-jwt`.
    sessionJwt?: string;
    regionalConfiguration: Configuration;
    endpointOverrideTests: ActiveTests;
    apiCalls: string[];
};

export type ResolveApiPathParams = {
    path: string;
    version?: string;
    includeVersion?: boolean;
};

type ResolveApiPathOptions = ResolveApiPathParams & {
    basePath?: string;
};

/** When true, cache key excludes auth cookie so entries are shared across users. */
export type FoundationRequestExtension = {
    nonPersonalized?: boolean;
};

export type FoundationRequestOptions = RequestOptions & FoundationRequestExtension;
export type FoundationGetRequest = GetRequest & FoundationRequestExtension;
export type FoundationHeadRequest = HeadRequest & FoundationRequestExtension;
export type FoundationPostRequest = PostRequest & FoundationRequestExtension;
export type FoundationPatchRequest = PatchRequest & FoundationRequestExtension;
export type FoundationPutRequest = PutRequest & FoundationRequestExtension;
export type FoundationDeleteRequest = DeleteRequest & FoundationRequestExtension;

/** Unspecified REST response shape when callers omit a generic (matches Apollo RESTDataSource). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Apollo RESTDataSource defaults GET/POST/etc. to TResult = any
type RestDataSourceDefaultResult = any;

class FoundationRestApi<Context extends BaseContext = BaseContext> extends RESTDataSource {
    protected contextValue: Context;
    // should be overwritten in implementing MFE
    protected logger?: Logger;
    // Canonical service identifier injected into every `trace()` log so that
    // request-trace logs remain attributable per-MFE in Datadog/New Relic after
    // the createLogger shim retirement. Subclasses set this to the value
    // previously passed to `createLogger(...)` (e.g. 'web_mf_account').
    protected lib?: string;

    constructor(options: { cache: KeyValueCache; contextValue: Context }) {
        super(options);
        this.contextValue = options.contextValue;
    }

    override resolveURL(path: string, request: AugmentedRequest) {
        if (!this.baseURL) {
            this.baseURL = this.getWebApiHost();
        }
        return super.resolveURL(path, request);
    }

    protected getWebApiHost() {
        const apiHost = this.contextValue.regionalConfiguration?.env?.API_HOST as
            | string
            | undefined;

        // Every GraphQL context defaults `regionalConfiguration` to `{}` and swallows a failed
        // config read, so an unguarded `.env.API_HOST` dies here with an opaque TypeError instead.
        if (!apiHost) {
            throwGraphQLError('Regional configuration unavailable: API_HOST is not set.', {
                response: { region: this.contextValue.region },
            });
        }

        return apiHost;
    }

    protected getContentApiHost() {
        const raw =
            (this.contextValue.regionalConfiguration?.env?.API_CONTENT_HOST as string) ?? '';
        const trimmed = raw.trim();
        return trimmed || this.getWebApiHost();
    }

    override willSendRequest(_path: string, requestOpts: AugmentedRequest): void {
        const processedOptions = this.getRequestOptions();

        if (processedOptions.headers) {
            Object.keys(processedOptions.headers).forEach((headerKey) => {
                requestOpts.headers[headerKey] = processedOptions?.headers?.[headerKey] as string;
            });
        }

        if (processedOptions.params) {
            Object.keys(processedOptions.params).forEach((paramKey) => {
                if (processedOptions?.params?.[paramKey]) {
                    requestOpts.params.set(paramKey, processedOptions.params[paramKey]);
                }
            });
        }
    }

    override async fetch<TResult>(path: string, incomingRequest: DataSourceRequest = {}) {
        const result = await super.fetch<TResult>(path, incomingRequest);

        // Record called APIs in the GraphQL context for debug extensions (APP_DEBUG environments)
        if (isAppDebug()) {
            this.contextValue.apiCalls = this.contextValue.apiCalls ?? [];
            this.contextValue.apiCalls.push(`${result.response.url}`);
        }

        return result;
    }

    resolvePath = ({
        path,
        version = DEFAULT_API_VERSION,
        includeVersion = true,
        basePath = '/api',
    }: ResolveApiPathOptions): string => {
        const endpointOverrideTests = this.contextValue.endpointOverrideTests;

        const overrideConfig = endpointOverridesConfig[path];

        // Path may be overriden for certain AB test/variant
        if (overrideConfig) {
            for (const testName in endpointOverrideTests) {
                const testConfig = overrideConfig[testName];
                if (testConfig && endpointOverrideTests[testName] !== undefined) {
                    const newPathObj = testConfig[endpointOverrideTests[testName]];
                    if (newPathObj) {
                        return `${basePath}/${newPathObj.version ?? version}/${newPathObj.path ?? path}`;
                    }
                }
            }
        }

        // Avoid appending version if it's in the path
        // Normalize path and version for comparison
        const normalizedPath = path.replace(/\/+/g, '/'); // Remove duplicate slashes
        const normalizedVersion = `/${version.replace(/^\/|\/$/g, '')}`; // Ensure version starts with a single slash
        if (normalizedPath.includes(normalizedVersion)) {
            // Check if normalized path already includes the version
            includeVersion = false;
        }

        if (!includeVersion) {
            path = `${basePath}/${path}`;
        } else {
            path = `${basePath}/${version}/${path}`;
        }
        // Avoid double '//' in the URLs
        return path.replace(/(?<!:)\/+/gm, '/');
    };

    resolveContentPath = (params: ResolveApiPathParams): string => {
        const raw =
            (this.contextValue.regionalConfiguration?.env?.API_CONTENT_HOST as string) ?? '';
        const hasContentHost = Boolean(raw.trim());
        return this.resolvePath({
            ...params,
            basePath: hasContentHost ? '/api/content' : '/api',
        });
    };

    protected getRequestOptions(additionalParams: Record<string, string> = {}) {
        const options: OptionsType = {};

        const cookieHeader = {};
        const cookieParts: string[] = [];

        if (typeof this.contextValue.authCookie === 'string') {
            cookieParts.push(`${AUTH_COOKIE_KEY}=${this.contextValue.authCookie}`);
        }

        const cbsDeviceIdCookie = this.contextValue.req.cookies?.CBS_DEVICEID;
        if (typeof cbsDeviceIdCookie === 'string') {
            cookieParts.push(`CBS_DEVICEID=${cbsDeviceIdCookie}`);
        }

        if (cookieParts.length) {
            Object.assign(cookieHeader, {
                cookie: cookieParts.join('; '),
            });
        }

        const fastlyHeaders = this.fastlyHeaders();

        options.headers = {
            ...cookieHeader,
            ...fastlyHeaders,
        };

        options.params = {
            platformType: getPlatformType(),
            _clientRegion: this.contextValue.region,
            ...additionalParams,
        };

        return options;
    }

    protected override async trace<TResult>(
        url: URL,
        request: RequestOptions,
        fn: () => Promise<TResult>,
    ): Promise<TResult> {
        const startTime = Date.now();
        try {
            return await fn();
        } finally {
            const duration = Date.now() - startTime;
            const label = `${request.method || 'GET'} ${url}`;
            if (this.lib) {
                this?.logger?.info(`${label} (${duration}ms)`, { lib: this.lib });
            } else {
                this?.logger?.info(`${label} (${duration}ms)`);
            }
        }
    }

    protected override get<TResult = RestDataSourceDefaultResult>(
        path: string,
        request?: FoundationGetRequest,
    ): Promise<TResult> {
        return super.get(path, request);
    }

    protected override head(path: string, request?: FoundationHeadRequest) {
        return super.head(path, request);
    }

    protected override post<TResult = RestDataSourceDefaultResult>(
        path: string,
        request?: FoundationPostRequest,
    ): Promise<TResult> {
        return super.post(path, request);
    }

    protected override patch<TResult = RestDataSourceDefaultResult>(
        path: string,
        request?: FoundationPatchRequest,
    ): Promise<TResult> {
        return super.patch(path, request);
    }

    protected override put<TResult = RestDataSourceDefaultResult>(
        path: string,
        request?: FoundationPutRequest,
    ): Promise<TResult> {
        return super.put(path, request);
    }

    protected override delete<TResult = RestDataSourceDefaultResult>(
        path: string,
        request?: FoundationDeleteRequest,
    ): Promise<TResult> {
        return super.delete(path, request);
    }

    protected override cacheKeyFor(url: URL, request: FoundationRequestOptions): string {
        if (request.cacheKey) {
            return request.cacheKey;
        }

        const authCookie = this.contextValue.authCookie;
        const authSuffix =
            request.nonPersonalized || typeof authCookie !== 'string' || authCookie === ''
                ? ''
                : ` ${authCookie}`;

        return `${request.method ?? 'GET'} ${url}${authSuffix}`;
    }

    /**
     * Get client data forwarded from Fastly
     *
     * @returns Object
     */
    private fastlyHeaders(): Record<string, string> {
        const clientRegion = this.contextValue.req.headers['HTTP_X_CLIENT_REGION']?.toString();
        const clientRegionSubdivision =
            this.contextValue.req.headers['HTTP_X_CLIENT_REGION_SUBDIVISION']?.toString();
        const clientCity = this.contextValue.req.headers['HTTP_X_CLIENT_CITY']?.toString();
        const clientCityLat = this.contextValue.req.headers['HTTP_X_CLIENT_CITY_LAT']?.toString();
        const clientCityLong = this.contextValue.req.headers['HTTP_X_CLIENT_CITY_LONG']?.toString();
        const clientCityLatLong = `${clientCityLat ?? ''}, ${clientCityLong ?? ''}`;
        const fastlyClientIp = this.contextValue.req.headers['HTTP_FASTLY_CLIENT_IP']?.toString();
        const clientLat = this.contextValue.req.headers['HTTP_X_CLIENT_LAT']?.toString();
        const clientLong = this.contextValue.req.headers['HTTP_X_CLIENT_LONG']?.toString();
        const userAgentHeader = this.contextValue.req.headers['user-agent'];
        const userAgent = Array.isArray(userAgentHeader)
            ? userAgentHeader.join(', ')
            : (userAgentHeader ?? '').toString();

        return {
            'X-Client-Region': clientRegion || '',
            'X-Client-Region-Subdivision': clientRegionSubdivision || '',
            'X-Client-City': clientCity || '',
            'X-Client-City-Lat-Long': clientCityLatLong,
            ...(clientLat === undefined ? {} : { 'X-Client-Lat': clientLat }),
            ...(clientLong === undefined ? {} : { 'X-Client-Long': clientLong }),
            'Fastly-Client-IP': fastlyClientIp || '',
            'User-Agent': userAgent,
        };
    }
}

export default FoundationRestApi;
